import type { DatasetStorageBackend } from "@/api/datasetStorageBackend";
import { ContentSwitcher } from "@carbon6/c6-frontend-design";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { EpisodeListFilters, EpisodeSummary } from "@/api";
import { EpisodeFilterLane } from "../EpisodeFilterLane";
import { EpisodeList } from "../EpisodeList";
import {
  excludedShownByFilters,
  filterVisibleEpisodes,
  shouldHideExcluded,
} from "../episodeReviewState";
import type { FileBrowserSource } from "../fileBrowserSource";
import { ExtraFileUploadAction } from "./ExtraFileUploadAction";
import { FilesSidebar } from "./FilesSidebar";
import type { EpisodePlayback } from "./preview/EpisodeVideoPreview";
import { PreviewPane } from "./preview/PreviewPane";
import { selectionReviewEpisode } from "./preview/previewSelection";
import { isFileSelection, type FolderSelection } from "./selection";
import { useEpisodeNav, type EpisodeNav } from "./useEpisodeNav";
import { useEpisodePage } from "../useEpisodePage";
import { useEpisodeReviewSavedRevision } from "../episodeReviewSavedStore";
import styles from "./FileBrowser.module.css";

export type { FolderSelection } from "./selection";
export type { EpisodeChangeGuard, EpisodeNav } from "./useEpisodeNav";

const EMPTY_EPISODES: readonly EpisodeSummary[] = [];

type FolderViewMode = "episodes" | "files";
const MODE_OPTIONS = [
  { value: "episodes", label: "에피소드" },
  { value: "files", label: "파일 목록" },
] as const;

export type FileBrowserProps = {
  // 데이터·write seam. directory-tree·episodes·extra·meta·signed-url 주입.
  source: FileBrowserSource;
  datasetUuid: string;
  // 선택 에피소드의 영상 아래에 추가 패널(진단 표면)을 그린다. 페이지(소비처)가 이 패널을
  // 주입하므로 components→page 역참조가 없다. 패널은 재생 핸들로 영상 시간축을 옮기고(진단
  // 그래프·구간 칩), 재생 위치를 구독하고, 타임라인 행(출처 구간)을 올린다. 영상은 이 컴포넌트가
  // 소유한다. 그래서 명령과 행만 내려보낸다.
  renderEpisodeExtra?: (episode: EpisodeSummary, playback: EpisodePlayback) => ReactNode;
  // 에피소드 목록 바로 위에 두는 소비처 슬롯. 메모처럼 브라우저 도메인이 소유하지 않는 액션을 둔다.
  episodeSidebarHeader?: ReactNode;
  // Files 모드 편집(업로드·이름변경·삭제) 게이트. write seam 이 있고 이 값이 true 면 노출한다.
  canModify?: boolean;
  // 이 Dataset 의 저장 위치. 추가 파일 업로드 창이 위치마다 다른 PUT 계약(크기 상한, 이미 있는
  // 경로의 거절)을 안내하는 데 쓴다. 모르면 생략한다.
  storageBackend?: DatasetStorageBackend;
  // 목록 행을 그리기 전에 적용하는 매퍼. 저장된 리뷰 override 를 다시 조회하지 않고 즉시 반영한다.
  mapEpisode?: (episode: EpisodeSummary) => EpisodeSummary;
  // 방금 저장된 에피소드. 그 행에 짧은 확인 표시를 띄운다. 이 표시는 리뷰 저장의 성공 신호다.
  flashEpisodeUuid?: string | null;
  // 우측 미리보기(콘텐츠) pane 하단에 추가하는 슬롯. 선택 에피소드의 리뷰 독을 둔다. 좌측 목록
  // 높이는 그대로 두고, 콘텐츠 아래에만 둔다(전체 폭 아님).
  renderEpisodeFooter?: (episode: EpisodeSummary, nav: EpisodeNav) => ReactNode;
  analysisLayout?: boolean;
  filesEnabled?: boolean;
  // 목록 배지에서 명시적으로 요청한 필터. 새 요청 객체가 들어올 때만 적용한다.
  initialEpisodeFilters?: EpisodeListFilters;
};

// 필터 축마다 리뷰 저장이 그 축의 값을 바꿀 수 있는지 적는다. 리뷰 쓰기 필드 중 필터 축에 닿는
// 것은 다섯 가지다: 검토 완료, 제외, 결함 수용, 실패, 결함 태그. 이 필드에서 값이 나오는 축만 true 다.
// 이름·길이·밴드·행동 출처·자동 검증은 저장과 무관하다. 정렬은 행 순서만 바꾸고 구성은 바꾸지
// 않으므로 표에 넣지 않는다. 필터 key 를 추가하면 이 표에 분류하기 전까지 typecheck 가 실패한다.
const REVIEW_AFFECTED_FILTER = {
  review_state: true,
  status: true,
  accepted_invalid: true,
  task_failed: true,
  quality_tag: true,
  quality_category: true,
  // 검토 완료가 품질 후보를 확인한 것으로 기록한다.
  quality_candidate: true,
  freeze_longest_min: false,
  freeze_longest_max: false,
  validation_status: false,
  // 사유는 자동 검증이 태깅한다. 제외·복원해도 바뀌지 않는다.
  invalid_reason: false,
  action_source: false,
  duration_min: false,
  duration_max: false,
  search: false,
  band_action_min: false,
  band_action_max: false,
  band_observation_min: false,
  band_observation_max: false,
  band_worst_channel: false,
} as const satisfies Record<Exclude<keyof EpisodeListFilters, "sort" | "order">, boolean>;

const REVIEW_AFFECTED_FILTER_KEYS = (
  Object.keys(REVIEW_AFFECTED_FILTER) as (keyof typeof REVIEW_AFFECTED_FILTER)[]
).filter((key) => REVIEW_AFFECTED_FILTER[key]);

// 리뷰 저장이 목록 구성을 바꿀 수 있는 축에 값이 하나라도 있는가.
function hasReviewAffectedFilter(filters: EpisodeListFilters): boolean {
  return REVIEW_AFFECTED_FILTER_KEYS.some((key) => {
    const value = filters[key];
    return Array.isArray(value) ? value.length > 0 : value !== undefined && value !== "";
  });
}

// 데이터셋 파일 브라우저. 좌측은 Episodes/Files 토글 사이드바, 우측은 미리보기다.
// - Episodes 모드: 에피소드 목록(page-based)과 선택 에피소드의 카메라 그리드/parquet.
// - Files 모드: canonical(채널)+extra 병합 디렉터리 트리와 채널/추가 파일 미리보기.
//   member+ 면 업로드/편집도 할 수 있다.
//
// 이 컴포넌트가 에피소드 목록 적재·숨김 필터·선택·키보드 이동을 소유한다. 하단 슬롯의 "다음
// 에피소드"가 좌측 목록과 **같은 보이는 순서**를 따라야 하기 때문이다.
export function FileBrowser({
  source,
  datasetUuid,
  renderEpisodeExtra,
  episodeSidebarHeader,
  canModify = false,
  storageBackend,
  mapEpisode,
  flashEpisodeUuid = null,
  renderEpisodeFooter,
  analysisLayout = false,
  filesEnabled = true,
  initialEpisodeFilters,
}: FileBrowserProps): ReactNode {
  const rootRef = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<FolderViewMode>("episodes");
  const lastEpisode = useRef<EpisodeSummary | null>(null);
  const [selection, setSelection] = useState<FolderSelection>(null);
  // owner write(업로드/이름변경/삭제) 후 디렉터리 트리·파일 목록을 다시 조회하게 하는 nonce.
  const [writeNonce, setWriteNonce] = useState(0);
  // 기본은 학습에서 제외된 에피소드를 감춰 리뷰 대상만 남긴다.
  const [includeExcluded, setIncludeExcluded] = useState(false);
  // 서버 리뷰 필터. 이 화면의 로컬 상태다(URL 에 넣지 않는다). 학습 제출 창의 미리보기와 같은
  // 레인 부품을 쓴다. 그래서 두 화면에서 조건을 적용하는 방법이 같다.
  const [filters, setFilters] = useState<EpisodeListFilters>(initialEpisodeFilters ?? {});
  // 필터는 그 필터를 건 데이터셋의 것이다. 라우트 페이지는 데이터셋이 바뀌어도 이 컴포넌트를 다시
  // 마운트하지 않는다. 그래서 데이터셋이 바뀌면 렌더 중에 필터를 비운다. effect 로 비우면 새 데이터셋을
  // 한 번은 이전 필터로 조회한다. 새 데이터셋의 필터는 호스트가 `initialEpisodeFilters` 로 다시 건다.
  const [filtersDatasetUuid, setFiltersDatasetUuid] = useState(datasetUuid);
  if (filtersDatasetUuid !== datasetUuid) {
    setFiltersDatasetUuid(datasetUuid);
    setFilters({});
  }

  useEffect(() => {
    if (initialEpisodeFilters) {
      setFilters(initialEpisodeFilters);
      setMode("episodes");
    }
  }, [initialEpisodeFilters]);

  const episodes = useEpisodePage(source, datasetUuid, { accumulate: true, filters });
  const { state: episodeState, loadMore, refresh } = episodes;

  // 리뷰 축 필터가 걸려 있으면 리뷰 저장이 목록 구성과 total 을 바꿀 수 있다. 저장한 행이 조건에서
  // 빠질 수 있기 때문이다. 그래서 불러온 범위를 제자리에서 다시 받는다. 리뷰와 무관한 축만 걸렸거나
  // 필터가 없으면 저장은 행 구성을 바꾸지 않는다. 행 값은 override(`mapEpisode`)가 이미 반영한다.
  const reviewRevision = useEpisodeReviewSavedRevision(datasetUuid);
  const filtered = hasReviewAffectedFilter(filters);
  const seenRevision = useRef({ datasetUuid, revision: reviewRevision });
  useEffect(() => {
    const seen = seenRevision.current;
    seenRevision.current = { datasetUuid, revision: reviewRevision };
    // 데이터셋을 바꾸면 세대 번호도 그 데이터셋의 값으로 바뀐다. 이것은 저장이 아니다.
    if (seen.datasetUuid !== datasetUuid || seen.revision === reviewRevision) return;
    if (filtered) void refresh();
  }, [datasetUuid, reviewRevision, filtered, refresh]);
  const loadedEpisodes = episodeState.status === "success" ? episodeState.data : EMPTY_EPISODES;
  // override 를 먼저 적용하고(저장 결과 즉시 반영), 그 다음에 숨김 필터를 적용한다.
  const mappedEpisodes = useMemo(
    () => (mapEpisode === undefined ? loadedEpisodes : loadedEpisodes.map(mapEpisode)),
    [loadedEpisodes, mapEpisode],
  );
  const hideExcluded = shouldHideExcluded(includeExcluded, filters);
  const items = useMemo(
    () => filterVisibleEpisodes(mappedEpisodes, !hideExcluded),
    [mappedEpisodes, hideExcluded],
  );
  // 서버 행 → 보이는 행. `items` 와 같은 규칙이다. 저장 뒤 새로 받은 행에서 다음 에피소드를 고를 때 쓴다.
  const projectEpisodes = useCallback(
    (rows: readonly EpisodeSummary[]) =>
      filterVisibleEpisodes(mapEpisode === undefined ? rows : rows.map(mapEpisode), !hideExcluded),
    [mapEpisode, hideExcluded],
  );

  // write 가 성공하면 트리·목록을 다시 조회하게 하고, **파일을 보고 있었을 때만** 선택을 비운다.
  // 삭제되거나 이름이 바뀐 파일을 참조하지 않기 위해서다. 에피소드 선택은 extra 파일 쓰기와
  // 무관하므로 그대로 둔다. 비우면 라벨링 독이 언마운트되어 미저장 편집이 확인 없이 사라진다.
  // 여기서는 가드로 묻지 않는다. 이 코드는 이미 성공한 쓰기의 후처리다. 그래서 사용자가 취소해도
  // 다시 조회는 해야 한다.
  const handleChanged = useCallback(() => {
    setSelection((current) => (isFileSelection(current) ? null : current));
    setWriteNonce((current) => current + 1);
  }, []);

  // 추가 파일 쓰기 게이트. canModify 와 write seam(declare) 이 모두 있어야 연다.
  const canUpload = canModify && source.declare !== undefined;

  // dataset 교체 시 모드·선택 초기화.
  useEffect(() => {
    setMode("episodes");
    setSelection(null);
    lastEpisode.current = null;
  }, [datasetUuid]);

  // 선택이 리뷰 표면을 붙일 대상인지, 그리고 그때 쓸 최신 행(저장 override 반영)을 구한다.
  // 판정은 `previewSelection` 이 소유한다. 그래서 좌측 목록 하이라이트와 우측 pane 이 같은 답을 쓴다.
  const reviewEpisode = useMemo(
    () => selectionReviewEpisode(selection, mappedEpisodes),
    [selection, mappedEpisodes],
  );
  const selectedEpisodeUuid = reviewEpisode?.uuid ?? null;

  // 전환 가드(리뷰 독의 미저장 확인). 가드가 등록되어 있으면 모든 전환 경로가 이 가드를 거친다.
  // 클릭·키보드·독의 이전/다음이 각자 따로 묻지 않게 확인을 한 곳으로 모은다.
  const changeGuardRef = useRef<Parameters<EpisodeNav["setChangeGuard"]>[0]>(null);
  const setChangeGuard = useCallback<EpisodeNav["setChangeGuard"]>((guard) => {
    changeGuardRef.current = guard;
  }, []);

  // 선택을 바꾸는 **유일한** 경로. 에피소드와 파일 모두 여기를 거친다. 에피소드 전환만 확인하고
  // 파일 선택을 확인하지 않으면, 미저장 라벨링이 있을 때 파일을 고르는 순간 편집이 확인 없이
  // 사라진다.
  const selectFolder = useCallback((next: FolderSelection) => {
    const apply = (): void => {
      if (next?.kind === "episode" || next?.kind === "channelVideo")
        lastEpisode.current = next.episode;
      setSelection(next);
    };
    const guard = changeGuardRef.current;
    if (guard !== null && !guard(apply)) {
      return;
    }
    apply();
  }, []);

  const selectEpisode = useCallback(
    (episode: EpisodeSummary) => selectFolder({ kind: "episode", episode }),
    [selectFolder],
  );

  // 첫 페이지가 도착하면 첫 에피소드를 자동 선택한다. 조건은 "에피소드가 안 골라졌다" 가 아니라
  // **"아무것도 안 골라졌다"** 다. 파일도 선택이다. 에피소드 유무로 판단하면 파일을 고르는 순간
  // 이 effect 가 선택을 첫 에피소드로 되돌린다. 그러면 파일을 열 수 없다.
  useEffect(() => {
    if (selection !== null) return;
    const first = items[0];
    if (first !== undefined) selectEpisode(first);
  }, [items, selection, selectEpisode]);

  // 파일 탐색을 끄는 분석 화면에서는 마지막 에피소드로 돌아간다.
  useEffect(() => {
    if (filesEnabled) return;
    setMode("episodes");
    if (selection?.kind === "episode") return;
    const episode = lastEpisode.current ?? items[0];
    if (episode) selectEpisode(episode);
  }, [filesEnabled, selection, items, selectEpisode]);

  const nav = useEpisodeNav({
    items,
    selectedEpisodeUuid,
    hasMore: episodes.hasMore,
    setChangeGuard,
    onSelect: selectEpisode,
    onLoadMore: loadMore,
    refresh,
    project: projectEpisodes,
    rootRef,
  });

  return (
    <div ref={rootRef} className={styles.root}>
      <div className={styles.sidebarColumn}>
        {mode === "episodes" && episodeSidebarHeader != null ? (
          <div className={styles.episodeSidebarHeader}>{episodeSidebarHeader}</div>
        ) : null}
        <aside className={styles.sidebar} aria-label="Dataset 브라우저">
          <div className={styles.modeToggle} hidden={!filesEnabled}>
            <ContentSwitcher
              label="브라우저 모드"
              size="sm"
              className={styles.modeSwitcher}
              options={MODE_OPTIONS}
              value={mode}
              onValueChange={(value) => setMode(value as FolderViewMode)}
            />
          </div>
          {/* 추가 파일 업로드는 모드와 무관하게 같은 위치에 둔다. extra 폴더 행에만 두면 에피소드
            모드에서 보이지 않고, extra 파일이 하나도 없는 데이터셋에는 진입점이 없다.
            버튼을 누르면 올린 파일이 보일 위치(파일 목록)로 먼저 옮긴다. */}
          {canUpload && filesEnabled ? (
            <div className={styles.uploadRow}>
              <ExtraFileUploadAction
                source={source}
                datasetUuid={datasetUuid}
                storageBackend={storageBackend}
                initialPath=""
                ariaLabel="추가 파일 업로드"
                title="올릴 폴더를 고른 뒤 파일을 업로드합니다."
                triggerText="추가 파일 업로드"
                onPress={() => setMode("files")}
                onUploaded={handleChanged}
              />
            </div>
          ) : null}
          {mode === "episodes" ? (
            // 에피소드 제외(excluded) 편집은 리뷰 독이 소유한다. 여기에는 읽기 전용 목록만 둔다.
            <div className={styles.listPane}>
              <EpisodeList
                state={episodeState}
                items={items}
                loadedCount={loadedEpisodes.length}
                total={episodes.total}
                hasMore={episodes.hasMore}
                loadingMore={episodes.loadingMore}
                includeExcluded={includeExcluded}
                onIncludeExcludedChange={setIncludeExcluded}
                includeExcludedForced={excludedShownByFilters(filters)}
                filters={
                  <EpisodeFilterLane
                    datasetUuid={datasetUuid}
                    value={filters}
                    onChange={setFilters}
                    disabled={episodeState.status === "loading"}
                  />
                }
                selectedUuid={selectedEpisodeUuid}
                flashUuid={flashEpisodeUuid}
                onSelect={selectEpisode}
                onLoadMore={loadMore}
                onRetry={episodes.reload}
              />
            </div>
          ) : (
            <FilesSidebar
              source={source}
              datasetUuid={datasetUuid}
              selection={selection}
              onSelect={selectFolder}
              canModify={canModify}
              storageBackend={storageBackend}
              writeNonce={writeNonce}
              onChanged={handleChanged}
            />
          )}
        </aside>
      </div>

      <PreviewPane
        source={source}
        datasetUuid={datasetUuid}
        selection={selection}
        reviewEpisode={reviewEpisode}
        canModify={canModify}
        onChanged={handleChanged}
        nav={nav}
        renderEpisodeExtra={renderEpisodeExtra}
        renderEpisodeFooter={renderEpisodeFooter}
        analysisLayout={analysisLayout}
      />
    </div>
  );
}
